import { useState } from "react";
import { Link } from "react-router-dom";
import { uploadFile } from "../api/client";
import { unwrap } from "../api/resources";
import { useToast } from "../context/ToastContext";
import { useBanks } from "../hooks/useBanks";
import {
  ACTIVE_PAYMENT_TYPES,
  SETTLE_PAYMENT_TYPES,
  bankDetail,
  bankLabel,
  paymentHint,
  paymentLabel,
  proofNumberLabel,
  requiresProof,
  setPaymentFields,
} from "../utils/finance";
import { COPY } from "../utils/copy";
import { Field, Input, Select } from "./ui";

export function PaymentTypeSelect({ value, onChange, mode = "active", required = true }) {
  const types = mode === "settle" ? SETTLE_PAYMENT_TYPES : ACTIVE_PAYMENT_TYPES;
  return (
    <Select required={required} value={value} onChange={onChange}>
      {types.map((type) => (
        <option key={type} value={type}>
          {paymentLabel(type)}
        </option>
      ))}
    </Select>
  );
}

export function ProofThumb({ url, label = "Picture" }) {
  if (!url) return null;
  return (
    <a className="proof-link" href={url} target="_blank" rel="noreferrer">
      {/\.(pdf)(\?|$)/i.test(url) ? label : <img src={url} alt={label} />}
    </a>
  );
}

export function ProofLine({ paymentType, referenceNumber, image, bank }) {
  const bankName = bankLabel(bank);
  if (!paymentType && !referenceNumber && !image && !bankName) return null;
  return (
    <span className="proof-line">
      {paymentType ? paymentLabel(paymentType) : ""}
      {bankName ? ` · ${bankName}` : ""}
      {referenceNumber ? ` · ${referenceNumber}` : ""}
      {image ? (
        <>
          {" · "}
          <a href={image} target="_blank" rel="noreferrer">
            Picture
          </a>
        </>
      ) : null}
    </span>
  );
}

export function ImageUpload({ value, onChange, disabled }) {
  const toast = useToast();
  const [busy, setBusy] = useState(false);

  const pick = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;
    setBusy(true);
    try {
      const data = unwrap(await uploadFile(file));
      onChange(data?.url || "");
    } catch (error) {
      toast.error(error.message || "Could not upload picture");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="image-upload">
      <input type="file" accept="image/jpeg,image/png,image/webp,application/pdf" disabled={disabled || busy} onChange={pick} />
      {busy ? <small>Uploading…</small> : null}
      {value ? (
        <div className="image-upload-preview">
          <ProofThumb url={value} />
          <button type="button" className="btn btn-ghost btn-sm" disabled={disabled} onClick={() => onChange("")}>
            Remove
          </button>
        </div>
      ) : null}
    </div>
  );
}

export function PaymentFields({
  form,
  onChange,
  mode = "active",
  required = true,
  label = "How paid?",
  disabled = false,
}) {
  const { banks, isBuilder } = useBanks();
  const needBank = requiresProof(form.paymentType);

  return (
    <>
      <Field label={label} hint={paymentHint(form.paymentType)}>
        <PaymentTypeSelect
          required={required}
          mode={mode}
          value={form.paymentType}
          onChange={(event) => onChange(setPaymentFields(form, event.target.value))}
        />
      </Field>
      {needBank ? (
        <>
          <Field label="Bank" hint={banks.length === 0 ? COPY.needBank : COPY.bankHint}>
            <Select
              required
              disabled={disabled}
              value={form.bankId || ""}
              onChange={(event) => onChange({ ...form, bankId: event.target.value })}
            >
              <option value="">{banks.length === 0 ? "No banks yet" : "Choose bank"}</option>
              {banks.map((bank) => (
                <option key={bank.id} value={bank.id}>
                  {bankDetail(bank) || bank.name}
                </option>
              ))}
            </Select>
          </Field>
          {banks.length === 0 && isBuilder ? (
            <p className="muted field-wide">
              <Link to="/banks">Add bank</Link>
            </p>
          ) : null}
          <Field label={proofNumberLabel(form.paymentType)}>
            <Input
              required
              disabled={disabled}
              value={form.referenceNumber || ""}
              onChange={(event) => onChange({ ...form, referenceNumber: event.target.value })}
              placeholder={proofNumberLabel(form.paymentType)}
            />
          </Field>
          <Field label="Picture" hint="Required for cheque, online and pay order" wide>
            <ImageUpload disabled={disabled} value={form.image || ""} onChange={(image) => onChange({ ...form, image })} />
            <input className="proof-required" tabIndex={-1} required value={form.image || ""} onChange={() => {}} />
          </Field>
        </>
      ) : null}
    </>
  );
}
